import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';

/**
 * Export template to Word document
 * @param {Object} template - The template object with blocks
 * @param {Object} values - The filled variable values
 * @param {string} filename - The filename (without .docx extension)
 */
export const exportToWord = async (template, values = {}, filename = 'document') => {
    if (!template?.blocks) {
        throw new Error('No template to export');
    }

    const children = [];

    // Add document title
    children.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: template.title || 'Legal Document',
                    bold: true,
                    size: 36, // 18pt
                    font: 'Times New Roman',
                }),
            ],
            heading: HeadingLevel.TITLE,
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
            border: {
                bottom: { style: BorderStyle.SINGLE, size: 12, color: '000000' },
            },
        })
    );

    // Process each block
    template.blocks.forEach((block) => {
        // Skip optional disabled blocks
        if (block.type === 'clause' && block.optional && !block.enabled) {
            return;
        }

        const text = block.content || block.text || '';

        // Replace variables
        const processedText = text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
            const value = values[key];
            return value !== undefined && value !== '' ? value : '_______________';
        });

        switch (block.type) {
            case 'heading':
                children.push(
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: processedText,
                                bold: true,
                                size: 28, // 14pt
                                font: 'Times New Roman',
                                allCaps: true,
                            }),
                        ],
                        heading: HeadingLevel.HEADING_2,
                        alignment: AlignmentType.CENTER,
                        spacing: { before: 400, after: 200 },
                        border: {
                            bottom: { style: BorderStyle.SINGLE, size: 6, color: '000000' },
                        },
                    })
                );
                break;

            case 'paragraph':
            case 'clause':
                // Split by newlines and create paragraphs
                const lines = processedText.split('\n');
                lines.forEach((line, index) => {
                    if (line.trim()) {
                        children.push(
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: line,
                                        size: 24, // 12pt
                                        font: 'Times New Roman',
                                    }),
                                ],
                                alignment: AlignmentType.JUSTIFIED,
                                spacing: { after: index === lines.length - 1 ? 200 : 100 },
                                indent: block.type === 'clause' ? { left: 720 } : undefined, // 0.5 inch indent for clauses
                            })
                        );
                    } else {
                        // Empty line
                        children.push(new Paragraph({ children: [] }));
                    }
                });
                break;

            case 'signature':
                children.push(
                    new Paragraph({
                        children: [],
                        spacing: { before: 600 },
                        border: {
                            top: { style: BorderStyle.SINGLE, size: 6, color: 'CCCCCC' },
                        },
                    })
                );
                const sigLines = processedText.split('\n');
                sigLines.forEach((line) => {
                    children.push(
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: line,
                                    size: 24,
                                    font: 'Times New Roman',
                                }),
                            ],
                            spacing: { after: 100 },
                        })
                    );
                });
                break;

            case 'pagebreak':
                children.push(
                    new Paragraph({
                        children: [],
                        pageBreakBefore: true,
                    })
                );
                break;

            default:
                break;
        }
    });

    // Create the document
    const doc = new Document({
        sections: [
            {
                properties: {
                    page: {
                        margin: {
                            top: 1440, // 1 inch
                            bottom: 1440,
                            left: 1080, // 0.75 inch
                            right: 1080,
                        },
                    },
                },
                children,
            },
        ],
    });

    // Generate and download
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${filename.replace(/[^a-zA-Z0-9-_]/g, '_')}.docx`);
};

export default exportToWord;
